// src/components/dashboard/HealthMetricsPage.tsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiService } from '@/services/api';
import { Chart, registerables } from 'chart.js';
import { NarrativeReportView } from './NarrativeReportView';

Chart.register(...registerables);

interface HealthMetric {
  id: string;
  metric_name: string;
  display_name: string;
  value: any; // Can be number or string
  numeric_value: number;
  unit: string;
  status: string;
  reference_range: string;
  reference_range_text: string;
  reference_min: number | null;
  reference_max: number | null;
  original_value?: string;
  extracted_at: string;
  measured_at?: string;
}

interface CategoryInsight {
  id: string;
  status: string;
  insight_summary: string;
  action_items: string[];
  metrics_count: number;
  category_type?: 'numeric' | 'narrative_lab' | 'diagnostic';
  category_name?: string;
  date_range: string;
  generated_at: string;
}

interface HealthMetricsPageProps {
  categoryName: string;
  onBack?: () => void;
  isDemo?: boolean;
}

interface MetricTimeline {
  metricName: string;
  displayName: string;
  unit: string;
  referenceRange: string;
  referenceMin: number | null;
  referenceMax: number | null;
  dataPoints: {
    date: string;
    value: number | string;
    status: string;
    timestamp: number;
  }[];
  latestValue: number | string;
  latestStatus: string;
  latestDate: string;
}

export const HealthMetricsPage: React.FC<HealthMetricsPageProps> = ({
  categoryName,
  onBack,
  isDemo = false
}) => {
  const queryClient = useQueryClient();
  const [metrics, setMetrics] = useState<HealthMetric[]>([]);
  const [categoryInsight, setCategoryInsight] = useState<CategoryInsight | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const [selectedReportDate, setSelectedReportDate] = useState<string | null>(null);
  const chartsRef = useRef<{ [key: string]: Chart }>({});

  useEffect(() => {
    loadCategoryData();
  }, [categoryName]);

  useEffect(() => {
    // Cleanup charts on unmount
    return () => {
      Object.values(chartsRef.current).forEach(chart => chart.destroy());
    };
  }, []);

  const loadCategoryData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (isDemo) {
        // Load from React Query cache correctly seeded by Orchestrator
        const mockData = queryClient.getQueryData<any>(['categoryDetail', categoryName]);
        if (mockData) {
          setMetrics(mockData.metrics || []);
          setCategoryInsight(mockData.category_insight);
        } else {
          // Fallback if not seeded yet matching api structure
          setMetrics([]);
          setCategoryInsight(null);
        }
      } else {
        const response = await apiService.getMetricsByCategory(categoryName);
        if (response.success && response.data) {
          setMetrics(response.data.metrics || []);
          setCategoryInsight(response.data.category_insight);
        }
      }
    } catch (err: any) {
      console.error('Failed to load category data:', err);
      if (err.message !== 'UNAUTHORIZED') {
        setError(err.message || 'Failed to load category data');
      }
    } finally {
      setLoading(false);
    }
  };

  const getMetricDate = (metric: HealthMetric): string => {
    return metric.measured_at || metric.extracted_at;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const month = date.toLocaleString('en-US', { month: '2-digit' });
    const day = date.toLocaleString('en-US', { day: '2-digit' });
    const year = date.getFullYear();
    return `${month} /${day}/${year} `;
  };

  const parseReferenceRange = (metric: HealthMetric): { min: number | null; max: number | null } => {
    const metricName = (metric.metric_name || '').toLowerCase();

    // Priority 1: Use numeric fields if both exist
    if (metric.reference_min !== null && metric.reference_max !== null) {
      return { min: metric.reference_min, max: metric.reference_max };
    }

    // Priority 2: Try to parse the text range string
    let rangeText = (metric.reference_range_text || metric.reference_range || '').trim();

    // If range is clearly qualitative (e.g., "Brown", "Negative", "Normal"), return nulls
    const qualitativeTerms = ['negative', 'normal', 'absent', 'present', 'positive', 'brown', 'yellow', 'trace', 'nil', 'nil/hpf'];
    if (qualitativeTerms.includes(rangeText.toLowerCase())) {
      return { min: null, max: null };
    }

    // Special check for truncated extraction like "<"
    if (rangeText === '<' || rangeText.toLowerCase() === 'normal: <') {
      if (metricName.includes('triglycerides')) rangeText = '< 150';
      else if (metricName.includes('cholesterol_total')) rangeText = '< 200';
      else if (metricName.includes('ldl')) rangeText = '< 100';
    }

    // Pattern: "< 150" or "<150"
    if (rangeText.includes('<') && !rangeText.includes('-')) {
      const match = rangeText.match(/<\s*([0-9.]+)/);
      if (match) {
        const val = parseFloat(match[1]);
        return { min: 0, max: val };
      }
    }

    // Pattern: "> 150" or ">150"
    if (rangeText.includes('>') && !rangeText.includes('-')) {
      const match = rangeText.match(/>\s*([0-9.]+)/);
      if (match) {
        const val = parseFloat(match[1]);
        return { min: val, max: val * 2 }; // Estimate max for visualization
      }
    }

    // Pattern: "100 - 150"
    const parts = rangeText.split('-').map(s => parseFloat(s.trim()));
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      return { min: parts[0], max: parts[1] };
    }

    // Priority 3: Use single numeric field fallbacks
    if (metric.reference_min === null && metric.reference_max !== null) {
      return { min: 0, max: metric.reference_max };
    }
    if (metric.reference_min !== null && metric.reference_max === null) {
      const estimatedMax = Math.max(metric.reference_min * 2, metric.reference_min + 100);
      return { min: metric.reference_min, max: estimatedMax };
    }

    // Final medical-based fallbacks for missing data
    if (metricName.includes('triglycerides')) return { min: 0, max: 150 };
    if (metricName.includes('cholesterol_total')) return { min: 0, max: 200 };

    // If nothing matches and it's not a common numeric metric, it's likely qualitative or missing
    // Return nulls instead of forcing a 0-100 range which is misleading
    return { min: null, max: null };
  };

  const metricsTimeline: MetricTimeline[] = useMemo(() => {
    const grouped = new Map<string, HealthMetric[]>();

    metrics.forEach(metric => {
      // Group by metric_name (normalized slug) to merge variations like "Hb" and "Hemoglobin"
      const key = metric.metric_name;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(metric);
    });


    const timelines: MetricTimeline[] = [];

    grouped.forEach((metricList) => {
      const sorted = metricList.sort((a, b) => {
        const timeA = new Date(getMetricDate(a)).getTime();
        const timeB = new Date(getMetricDate(b)).getTime();
        if (timeA !== timeB) return timeA - timeB;

        // Tie-breaker: use extracted_at (process date) for same-day measurements
        const extractA = a.extracted_at ? new Date(a.extracted_at).getTime() : 0;
        const extractB = b.extracted_at ? new Date(b.extracted_at).getTime() : 0;
        return extractA - extractB;
      });

      const dataPoints = sorted.map(m => ({
        date: formatDate(getMetricDate(m)),
        value: m.numeric_value || m.value,
        status: m.status,
        timestamp: new Date(getMetricDate(m)).getTime()
      }));

      const latest = sorted[sorted.length - 1];
      const { min, max } = parseReferenceRange(latest);

      timelines.push({
        metricName: latest.metric_name,
        displayName: latest.display_name,
        unit: latest.unit,
        referenceRange: latest.reference_range_text || latest.reference_range,
        referenceMin: min,
        referenceMax: max,
        dataPoints,
        latestValue: latest.numeric_value || latest.value,
        latestStatus: latest.status,
        latestDate: formatDate(getMetricDate(latest))
      });
    });

    return timelines;
  }, [metrics]);

  const uniqueDates = useMemo(() => {
    const dates = new Set<string>();
    metrics.forEach(m => {
      dates.add(formatDate(getMetricDate(m)));
    });
    return Array.from(dates).sort((a, b) => {
      return new Date(b).getTime() - new Date(a).getTime();
    });
  }, [metrics]);

  const getValuePosition = (value: number, min: number, max: number): number => {
    if (max === min) return 50;
    const position = ((value - min) / (max - min)) * 100;
    return Math.max(0, Math.min(100, position));
  };

  const getStatusText = (status: string): string => {
    const normalizedStatus = status?.toLowerCase();
    if (normalizedStatus === 'normal') return 'Normal';
    if (normalizedStatus === 'low') return 'Below range';
    if (normalizedStatus === 'high') return 'Above range';
    if (normalizedStatus === 'borderline') return 'Borderline';
    if (normalizedStatus === 'critical') return 'Critical';
    return 'Unknown';
  };

  const getStatusColor = (status: string): string => {
    switch (status?.toLowerCase()) {
      case 'normal':
        return 'var(--color-success)';
      case 'borderline':
      case 'high':
      case 'low':
        return 'var(--color-warning)';
      case 'critical':
        return 'var(--color-error)';
      default:
        return 'var(--color-secondary)';
    }
  };

  const getStatusBoxClass = (status: string): 'good' | 'warning' | 'danger' => {
    const normalizedStatus = status?.toLowerCase();
    if (normalizedStatus === 'critical') return 'danger';
    if (normalizedStatus === 'borderline' || normalizedStatus === 'high' || normalizedStatus === 'low') return 'warning';
    if (normalizedStatus === 'normal') return 'good';
    return 'good';
  };

  const getStatusBoxTitle = (status: string): string => {
    const normalizedStatus = status?.toLowerCase();
    if (normalizedStatus === 'critical') return 'Critical level';
    if (normalizedStatus === 'low') return 'Below normal range';
    if (normalizedStatus === 'high') return 'Above normal range';
    if (normalizedStatus === 'borderline') return 'Borderline level';
    if (normalizedStatus === 'normal') return 'In the normal range';
    return 'Status unknown';
  };

  const getStatusBoxText = (status: string): string => {
    const normalizedStatus = status?.toLowerCase();
    if (normalizedStatus === 'critical') return 'Consult with your healthcare provider immediately.';
    if (normalizedStatus === 'low') return 'Consider discussing with your healthcare provider about potential causes and solutions.';
    if (normalizedStatus === 'high') return 'Consider discussing with your healthcare provider about potential causes and management.';
    if (normalizedStatus === 'borderline') return 'Your levels are slightly outside the normal range. Consider lifestyle modifications and discuss with your healthcare provider.';
    if (normalizedStatus === 'normal') return 'Your levels are healthy. Maintain current habits.';
    return 'Continue monitoring your levels.';
  };

  // Format display name to show both original and standard name when they differ
  // e.g., "TLC" with metric_name "wbc" -> "TLC (WBC)"
  const formatMetricDisplayName = (displayName: string, metricName: string): string => {
    if (!displayName || !metricName) return displayName || metricName || 'Unknown';

    // Normalize both for comparison (lowercase, remove special chars)
    const normalizedDisplay = displayName.toLowerCase().replace(/[^a-z0-9]/g, '');
    const normalizedMetric = metricName.toLowerCase().replace(/[^a-z0-9]/g, '');

    // If they're essentially the same, just show display name
    if (normalizedDisplay === normalizedMetric || normalizedDisplay.includes(normalizedMetric)) {
      return displayName;
    }

    // Format the standard name nicely (wbc -> WBC, hemoglobin -> Hemoglobin)
    const standardName = metricName
      .split('_')
      .map(word => word.length <= 3 ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    return `${displayName} (${standardName})`;
  };

  const calculateStats = (dataPoints: MetricTimeline['dataPoints']) => {
    const values = dataPoints.map(d => d.value);
    // Only calculate for numeric values
    const numericValues = values.filter(v => typeof v === 'number') as number[];
    if (numericValues.length === 0) return null;

    const avg = numericValues.reduce((a, b) => a + b, 0) / numericValues.length;
    const min = Math.min(...numericValues);
    const max = Math.max(...numericValues);
    const stdDev = Math.sqrt(numericValues.reduce((sum, val) => sum + Math.pow(val - avg, 2), 0) / numericValues.length);

    // Calculate trend
    const first = numericValues[0];
    const last = numericValues[numericValues.length - 1];
    const percentChange = first !== 0 ? ((last - first) / first) * 100 : 0;

    let trend = 'stable';
    let trendSymbol = '→';
    if (percentChange > 5) {
      trend = 'up';
      trendSymbol = '↗';
    } else if (percentChange < -5) {
      trend = 'down';
      trendSymbol = '↘';
    }

    return { avg, min, max, stdDev, trend, trendSymbol, percentChange };
  };

  const toggleCard = (metricName: string, metric: MetricTimeline) => {
    if (expandedCard === metricName) {
      setExpandedCard(null);
    } else {
      setExpandedCard(metricName);
      // Wait for DOM to update, then create chart
      setTimeout(() => {
        createChart(metricName, metric);
      }, 100);
    }
  };

  const createChart = (metricName: string, metric: MetricTimeline) => {
    const canvas = document.getElementById(`chart-${metricName}`) as HTMLCanvasElement;
    if (!canvas) return;

    // Destroy existing chart
    if (chartsRef.current[metricName]) {
      chartsRef.current[metricName].destroy();
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Get computed styles for theme colors
    const computedStyles = getComputedStyle(document.documentElement);
    const textColor = computedStyles.getPropertyValue('--color-text').trim();
    const borderColor = computedStyles.getPropertyValue('--color-border').trim();
    const successColor = computedStyles.getPropertyValue('--color-success').trim();

    const labels = metric.dataPoints.map(d => {
      const date = new Date(d.timestamp);
      return `${date.toLocaleString('en-US', { month: 'short' })} ${date.getFullYear()}`;
    });
    const values = metric.dataPoints.map(d => d.value);
    const numericValues = values.filter(v => typeof v === 'number') as number[];
    if (numericValues.length === 0) return;

    // Calculate adaptive Y-axis bounds to always show the normal range
    const dataMin = Math.min(...numericValues);
    const dataMax = Math.max(...numericValues);
    const refMin = metric.referenceMin ?? dataMin;
    const refMax = metric.referenceMax ?? dataMax;

    const yMin = Math.min(dataMin, refMin) * 0.9;
    const yMax = Math.max(dataMax, refMax) * 1.1;

    chartsRef.current[metricName] = new Chart(ctx, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [{
          label: 'Value',
          data: numericValues,
          borderColor: successColor || '#10b981',
          backgroundColor: `${successColor || '#10b981'} 10`,
          borderWidth: 3,
          fill: false,
          tension: 0.4,
          pointBackgroundColor: (context) => {
            const dataPoint = metric.dataPoints[context.dataIndex];
            const status = dataPoint.status.toLowerCase();
            if (status === 'normal') return successColor || '#10b981';
            return 'var(--color-warning)';
          },
          pointBorderColor: textColor || '#fff',
          pointBorderWidth: 2,
          pointRadius: 6,
          pointHoverRadius: 8
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            backgroundColor: textColor === '#ffffff' ? '#1a1a1a' : '#1a202c',
            padding: 12,
            titleFont: {
              size: 13,
              weight: 600 as any
            },
            bodyFont: {
              size: 12
            },
            callbacks: {
              label: function (context: any) {
                const dataPoint = metric.dataPoints[context.dataIndex];
                return [`Value: ${context.parsed.y} ${metric.unit} `, `Status: ${getStatusText(dataPoint.status)} `];
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: false,
            min: yMin,
            max: yMax,
            grid: {
              color: borderColor || '#e5e7eb'
            },
            ticks: {
              font: {
                size: 11
              },
              color: textColor || '#666',
              callback: function (value: any) {
                return value + ' ' + metric.unit;
              }
            }
          },
          x: {
            grid: {
              display: false
            },
            ticks: {
              font: {
                size: 10
              },
              color: textColor || '#666',
              maxRotation: 45,
              minRotation: 45
            }
          }
        },
        interaction: {
          intersect: false,
          mode: 'index'
        }
      },
      plugins: [{
        id: 'normalCorridor',
        beforeDraw: (chart) => {
          if (metric.referenceMin === null || metric.referenceMax === null) return;
          const { ctx, chartArea: { top, bottom, left, right }, scales: { y } } = chart;
          const startY = y.getPixelForValue(metric.referenceMin);
          const endY = y.getPixelForValue(metric.referenceMax);

          ctx.save();
          ctx.fillStyle = `${successColor || '#10b981'} 15`;
          ctx.fillRect(left, Math.min(startY, endY), right - left, Math.abs(startY - endY));
          ctx.restore();
        }
      }]
    });
  };

  const createSparkline = (metric: MetricTimeline) => {
    const numericValues = metric.dataPoints.map(d => d.value).filter(v => typeof v === 'number') as number[];
    if (numericValues.length < 2) return null;

    const min = Math.min(...numericValues);
    const max = Math.max(...numericValues);
    const range = Math.max(max - min, 1); // Avoid division by zero
    const width = 300;
    const height = 30;

    const points = numericValues.map((val, i) => {
      const x = (i / (numericValues.length - 1)) * width;
      const y = height - ((val - min) / range) * height;
      return `${x},${y} `;
    }).join(' ');

    return (
      <svg width="100%" height="30" viewBox={`0 0 ${width} ${height} `} preserveAspectRatio="none">
        <polyline
          points={points}
          fill="none"
          stroke="var(--color-success)"
          strokeWidth="2"
          opacity="0.8"
        />
        {numericValues.map((val, i) => {
          const x = (i / (numericValues.length - 1)) * width;
          const y = height - ((val - min) / range) * height;
          return <circle key={i} cx={x} cy={y} r="2" fill="var(--color-success)" />;
        })}
      </svg>
    );
  };

  const parseInsights = (summary: string): { type: 'success' | 'warning' | 'info'; text: string }[] => {
    const insights: { type: 'success' | 'warning' | 'info'; text: string }[] = [];
    const sentences = summary.split(/(?<=[.!])\s+/).filter(s => s.trim());

    sentences.forEach((sentence, index) => {
      const lower = sentence.toLowerCase();
      if (lower.includes('great') || lower.includes('normal') || lower.includes('healthy') || lower.includes('good') || lower.includes('wonderful') || lower.includes('excellent')) {
        insights.push({ type: 'success', text: sentence });
      } else if (lower.includes('low') || lower.includes('high') || lower.includes('attention') || lower.includes('monitor') || lower.includes('consider')) {
        insights.push({ type: 'warning', text: sentence });
      } else if (lower.includes('retest') || lower.includes('recommend') || lower.includes('suggest')) {
        insights.push({ type: 'info', text: sentence });
      } else if (index === 0) {
        insights.push({ type: 'success', text: sentence });
      } else {
        insights.push({ type: 'info', text: sentence });
      }
    });

    return insights;
  };

  const getInsightStatusBadge = (status: string): { text: string; color: string } => {
    const normalized = status?.toLowerCase().replace('_', ' ');
    switch (normalized) {
      case 'optimal':
      case 'normal':
        return { text: 'Normal', color: 'var(--color-success)' };
      case 'needs attention':
      case 'attention':
        return { text: 'Needs Attention', color: 'var(--color-warning)' };
      case 'critical':
        return { text: 'Critical', color: 'var(--color-error)' };
      default:
        return { text: 'High Confidence', color: 'var(--color-success)' };
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        fontFamily: 'var(--font-primary)'
      }}>
        <p style={{ color: 'var(--color-muted)' }}>Loading metrics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        fontFamily: 'var(--font-primary)',
        gap: 'var(--space-4)'
      }}>
        <p style={{ color: 'var(--color-error)' }}>Error: {error}</p>
        <button
          onClick={loadCategoryData}
          style={{
            padding: 'var(--space-2) var(--space-4)',
            backgroundColor: 'var(--color-primary)',
            color: 'var(--color-background)',
            border: 'none',
            borderRadius: 'var(--radius)',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  const statusBadge = categoryInsight ? getInsightStatusBadge(categoryInsight.status) : null;
  const insights = categoryInsight ? parseInsights(categoryInsight.insight_summary) : [];

  return (
    <div style={{
      fontFamily: 'var(--font-primary)',
      backgroundColor: 'var(--color-background)',
      minHeight: '100vh',
      color: 'var(--color-text)',
      position: 'relative'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '40px',
        paddingTop: '20px'
      }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{
            fontSize: '28px',
            fontWeight: 600,
            marginBottom: '4px',
            color: 'var(--color-text)'
          }}>
            {categoryName}
          </h1>
          <p style={{
            color: 'var(--color-muted)',
            fontSize: '13px',
            margin: 0
          }}>
            {metricsTimeline.length} unique {metricsTimeline.length === 1 ? 'metric' : 'metrics'} tracked over {uniqueDates.length} {uniqueDates.length === 1 ? 'report' : 'reports'}
          </p>
        </div>

        {/* AI Analysis Section */}
        {categoryInsight && (
          <div style={{
            background: 'var(--color-surface)',
            borderRadius: '8px',
            padding: '24px',
            marginBottom: '32px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{
                fontSize: '16px',
                fontWeight: 600,
                margin: 0,
                color: 'var(--color-text)'
              }}>
                AI Health Analysis
              </h2>
              {statusBadge && (
                <span style={{
                  background: 'var(--color-success)',
                  color: 'var(--color-background)',
                  padding: '4px 12px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  display: 'inline-flex',
                  alignItems: 'center',
                  opacity: 0.9
                }}>
                  <span style={{ marginRight: '6px', fontSize: '8px' }}>●</span>
                  {statusBadge.text}
                </span>
              )}
            </div>

            {insights.map((insight, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  marginBottom: index === insights.length - 1 ? 0 : '12px',
                  fontSize: '14px',
                  lineHeight: 1.6
                }}
              >
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '12px',
                  flexShrink: 0,
                  fontSize: '12px',
                  fontWeight: 'bold',
                  background: insight.type === 'success' ? 'var(--color-success)' : insight.type === 'warning' ? 'var(--color-warning)' : 'var(--color-accent-blue)',
                  color: 'var(--color-background)',
                  opacity: 0.9
                }}>
                  {insight.type === 'success' ? '✓' : insight.type === 'warning' ? '!' : '💡'}
                </div>
                <div style={{ color: 'var(--color-text)' }}>{insight.text}</div>
              </div>
            ))}
          </div>
        )}

        {/* Narrative Level 3: Detailed Report View */}
        {selectedReportDate ? (
          <NarrativeReportView
            title={`${categoryInsight?.category_name || categoryName} Report`}
            date={selectedReportDate}
            metrics={metrics.filter(m => formatDate(getMetricDate(m)) === selectedReportDate)}
            onBack={() => setSelectedReportDate(null)}
          />
        ) : (
          /* Metrics Section */
          metricsTimeline.length > 0 && (
            <div>
              <h2 style={{
                fontSize: '16px',
                fontWeight: 600,
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
                color: 'var(--color-text)'
              }}>
                <span style={{ marginRight: '8px' }}>
                  {(categoryInsight?.category_type === 'narrative_lab' || categoryInsight?.category_type === 'diagnostic') ? '📋' : '📈'}
                </span>
                {(categoryInsight?.category_type === 'narrative_lab' || categoryInsight?.category_type === 'diagnostic') ? 'Report History' : 'Metric Trends Over Time'}
              </h2>

              <div className="metrics-grid-container" style={
                (categoryInsight?.category_type === 'narrative_lab' || categoryInsight?.category_type === 'diagnostic')
                  ? { gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }
                  : {}
              }>
                {(categoryInsight?.category_type === 'narrative_lab' || categoryInsight?.category_type === 'diagnostic') ? (
                  /* Level 2: Narrative Report History Grid */
                  uniqueDates.map(date => {
                    const reportMetrics = metrics.filter(m => formatDate(getMetricDate(m)) === date);
                    return (
                      <div
                        key={date}
                        className="metric-card narrative-card"
                        style={{ height: 'auto', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
                        onClick={() => setSelectedReportDate(date)}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{date}</span>
                          <span className="readings-badge">{reportMetrics.length} results</span>
                        </div>

                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: 'var(--space-2)' }}>
                          Findings include: {reportMetrics.slice(0, 3).map(m => m.display_name).join(', ')}
                          {reportMetrics.length > 3 ? '...' : ''}
                        </div>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedReportDate(date);
                          }}
                          style={{
                            width: '100%',
                            padding: 'var(--space-2)',
                            backgroundColor: 'rgba(57, 107, 240, 0.1)',
                            color: 'var(--color-primary)',
                            border: 'none',
                            borderRadius: 'var(--radius)',
                            fontWeight: 600,
                            marginTop: 'var(--space-2)',
                            cursor: 'pointer'
                          }}
                        >
                          View Full Report
                        </button>
                      </div>
                    );
                  })
                ) : (
                  /* Level 2: Numeric Trends Grid */
                  metricsTimeline.map((metric) => {
                    const isSingleReading = metric.dataPoints.length === 1;
                    const isExpanded = expandedCard === metric.metricName;
                    const stats = !isSingleReading ? calculateStats(metric.dataPoints) : null;

                    const min = metric.referenceMin;
                    const max = metric.referenceMax;
                    const value = metric.latestValue;
                    const isNumeric = typeof value === 'number';

                    // Smart Scaling: Ensure the scale accommodates both the range AND the current value
                    // If not numeric, these won't be used for the bar position
                    const visualMin = isNumeric ? Math.min(min ?? (value as number), (value as number) * 0.9, 0) : 0;
                    const visualMax = isNumeric ? Math.max(max ?? (value as number), (value as number) * 1.1) : 100;

                    const valuePosition = isNumeric ? getValuePosition(value as number, visualMin, visualMax) : 0;
                    const normalEndPercent = (isNumeric && max !== null) ? getValuePosition(max, visualMin, visualMax) : 0;
                    const normalStartPercent = (isNumeric && min !== null) ? getValuePosition(min, visualMin, visualMax) : 0;

                    const statusText = getStatusText(metric.latestStatus);
                    const statusColor = getStatusColor(metric.latestStatus);
                    const cardStatusClass = getStatusBoxClass(metric.latestStatus);
                    const statusBoxTitle = getStatusBoxTitle(metric.latestStatus);
                    const statusBoxText = getStatusBoxText(metric.latestStatus);

                    // Clean Reference Range Text for display
                    let displayRange = metric.referenceRange || '';
                    if (displayRange === '<' || displayRange.toLowerCase() === 'normal: <') {
                      displayRange = `< ${max} `;
                    } else if (!displayRange && metric.referenceMin !== undefined) {
                      displayRange = `${metric.referenceMin} -${metric.referenceMax} `;
                    }

                    return (
                      <div
                        key={`${metric.metricName}-${metric.displayName}`}
                        className={`metric-card ${isExpanded ? 'expanded' : ''} ${isSingleReading ? 'single-reading' : ''}`}
                        onClick={() => !isSingleReading && toggleCard(metric.metricName, metric)}
                      >
                        {!isSingleReading && isExpanded && (
                          <button
                            className="close-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedCard(null);
                            }}
                          >
                            ×
                          </button>
                        )}

                        {/* Metric Header */}
                        <div className="metric-header">
                          <div>
                            <div className="metric-title">{formatMetricDisplayName(metric.displayName, metric.metricName)}</div>
                            {isSingleReading ? (
                              <span className="single-reading-badge">Single Test</span>
                            ) : (
                              <span className="readings-badge">{metric.dataPoints.length} readings</span>
                            )}
                          </div>
                          {!isSingleReading && <span className="expand-icon">{isExpanded ? '▲' : '▼'}</span>}
                        </div>

                        {/* Card Content - Different for Expanded vs Collapsed */}
                        {!isExpanded && (
                          <div className="metric-summary">
                            {!isSingleReading ? (
                              <>
                                <div className="current-value-label">CURRENT VALUE</div>
                                <div className="current-value">
                                  {value} <span className="current-value-unit">{metric.unit}</span>
                                </div>
                                <div className="value-status" style={{ color: statusColor }}>{statusText}</div>
                                <div className="sparkline-preview">
                                  {createSparkline(metric)}
                                </div>
                                {stats && (
                                  <div className="quick-stats">
                                    <div className="quick-stat">
                                      <div className="quick-stat-label">Trend</div>
                                      <div className="quick-stat-value" style={{
                                        color: stats.trend === 'up' ? 'var(--color-success)' : stats.trend === 'down' ? 'var(--color-error)' : 'var(--color-secondary)'
                                      }}>
                                        {stats.trend === 'up' ? 'Rising ↗' : stats.trend === 'down' ? 'Falling ↘' : 'Stable →'}
                                      </div>
                                    </div>
                                    <div className="quick-stat">
                                      <div className="quick-stat-label">Avg</div>
                                      <div className="quick-stat-value">{stats.avg.toFixed(1)}</div>
                                    </div>
                                    <div className="quick-stat">
                                      <div className="quick-stat-label">Range</div>
                                      <div className="quick-stat-value">{stats.min.toFixed(0)}-{stats.max.toFixed(0)}</div>
                                    </div>
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="single-reading-content">
                                <div className={`reading-circle ${cardStatusClass}`}>
                                  <div className="reading-circle-value">{value}</div>
                                  <div className="reading-circle-unit">{metric.unit}</div>
                                  <div className={`reading-circle-status ${cardStatusClass}`}>
                                    <span className="status-indicator"></span>
                                    {statusText}
                                  </div>
                                </div>
                                <div className="single-reading-info">
                                  <div className="single-info-item">
                                    <div className="single-info-label">Test Date</div>
                                    <div className="single-info-value">{(() => {
                                      const lastDp = metric.dataPoints[metric.dataPoints.length - 1];
                                      const date = new Date(lastDp.timestamp);
                                      return `${date.toLocaleString('en-US', { month: 'short' })} ${date.getFullYear()}`;
                                    })()}</div>
                                  </div>
                                  <div className="single-info-item">
                                    <div className="single-info-label">Reference Range</div>
                                    <div className="single-info-value">{displayRange}</div>
                                  </div>
                                </div>
                                <div className="no-trend-message">
                                  💡 This metric has only been tested once. Schedule follow-up tests to track trends over time.
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Detailed Layout when expanded */}
                        {!isSingleReading && isExpanded && (
                          <div className="metric-detailed">
                            <div className="trend-header">
                              <div className="trend-title">All Readings (Past 12 Months)</div>
                              {stats && (
                                <div className={`trend-indicator ${stats.trend}`}>
                                  <span className="trend-arrow">{stats.trendSymbol}</span>
                                  <span>
                                    {stats.trend === 'stable' ? 'Stable Overall' :
                                      stats.percentChange > 0 ? `+ ${stats.percentChange.toFixed(0)}% Improvement` :
                                        `${stats.percentChange.toFixed(0)}% Change`}
                                  </span>
                                </div>
                              )}
                            </div>

                            <div className="chart-canvas-container">
                              <canvas id={`chart-${metric.metricName}`}></canvas>
                            </div>

                            <div className="detailed-timeline-grid">
                              {metric.dataPoints.map((dp, index) => {
                                const isLatest = index === metric.dataPoints.length - 1;
                                return (
                                  <div key={index} className={`timeline-reading ${isLatest ? 'current' : ''}`}>
                                    <div className="reading-date">{(() => {
                                      const date = new Date(dp.timestamp);
                                      return `${date.toLocaleString('en-US', { month: 'short' })} ${date.getFullYear()}`;
                                    })()}</div>
                                    <div className="reading-value">{dp.value}</div>
                                    <div className="reading-unit">{metric.unit}</div>
                                    <div className={`reading-status ${dp.status.toLowerCase()}`}>
                                      {dp.status.toLowerCase() === 'normal' ? '✓ Normal' :
                                        dp.status.toLowerCase() === 'low' ? '⚠ Low' :
                                          dp.status.toLowerCase() === 'high' ? '⚠ High' :
                                            '✗ Out'}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                            {stats && (
                              <div className="mini-stats">
                                <div className="mini-stat">
                                  <div className="mini-stat-label">Lowest</div>
                                  <div className="mini-stat-value low">{stats.min.toFixed(1)}</div>
                                </div>
                                <div className="mini-stat">
                                  <div className="mini-stat-label">Average</div>
                                  <div className="mini-stat-value">{stats.avg.toFixed(1)}</div>
                                </div>
                                <div className="mini-stat">
                                  <div className="mini-stat-label">Highest</div>
                                  <div className="mini-stat-value high">{stats.max.toFixed(1)}</div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Range Visualization (Shared) - Only if numeric and range exists */}
                        {isNumeric && min !== null && max !== null && (
                          <div className="range-visualization">
                            <div className="range-labels">
                              <span>REFERENCE RANGE</span>
                              <span>{displayRange}</span>
                            </div>
                            <div
                              className="range-bar"
                              style={{
                                background: displayRange.includes('<')
                                  ? `linear-gradient(to right, var(--color-success) 0%, var(--color-success) ${normalEndPercent}%, var(--color-error) ${normalEndPercent}%, var(--color-error) 100%)`
                                  : displayRange.includes('>')
                                    ? `linear-gradient(to right, var(--color-error) 0%, var(--color-error) ${normalStartPercent}%, var(--color-success) ${normalStartPercent}%, var(--color-success) 100%)`
                                    : `linear-gradient(to right, var(--color-error) 0%, var(--color-error) ${normalStartPercent}%, var(--color-success) ${normalStartPercent}%, var(--color-success) ${normalEndPercent}%, var(--color-error) ${normalEndPercent}%, var(--color-error) 100%)`
                              }}
                            >
                              <div className="range-marker" style={{ left: `${valuePosition}%` }} />
                            </div>
                            <div className="range-values">
                              <span>{Math.round(visualMin)}</span>
                              <span>{Math.round(visualMax)}</span>
                            </div>
                          </div>
                        )}

                        {/* Qualitative Range Info - If not numeric or no numeric range */}
                        {(!isNumeric || min === null || max === null) && (
                          <div className="range-visualization qualitative">
                            <div className="range-labels">
                              <span>REFERENCE RANGE</span>
                              <span>{displayRange || 'Not specified'}</span>
                            </div>
                            <div className="range-bar-qualitative">
                              <div className={`range-status-indicator ${cardStatusClass}`} />
                              <span>{statusText}</span>
                            </div>
                          </div>
                        )}

                        {/* Status Box (Shared) */}
                        <div className={`status-box ${cardStatusClass}`}>
                          <div className="status-box-title">{statusBoxTitle}</div>
                          <div className="status-box-text">{statusBoxText}</div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )
        )}
      </div>

      {/* Styles */}
      <style>{`
.metrics-grid-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

.metric-card {
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 20px;
  background: var(--color-card-bg);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  animation: fadeInUp 0.5s ease forwards;
}

.metric-card:hover {
  box-shadow: var(--shadow-lg);
  transform: translateY(-2px);
}

.metric-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--color-accent-blue), var(--color-accent-purple), var(--color-accent-red));
  opacity: 0;
  transition: opacity 0.3s ease;
}

.metric-card:hover::before {
  opacity: 1;
}

.metric-card.expanded {
  grid-column: 1 / -1;
  cursor: default;
}

.metric-card.expanded:hover {
  transform: none;
}

.metric-card.single-reading {
  cursor: default;
}

.metric-card.single-reading:hover {
  transform: none;
}

.metric-card.single-reading::before {
  opacity: 0.3;
}

.metric-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}

.metric-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 8px;
}

.expand-icon {
  font-size: 20px;
  color: var(--color-accent-blue);
  transition: transform 0.3s ease;
  user-select: none;
}

.metric-card.expanded .expand-icon {
  transform: rotate(180deg);
}

.readings-badge {
  display: inline-flex;
  align-items: center;
  background: var(--color-accent-blue-bg);
  color: var(--color-accent-blue);
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}

.readings-badge::before {
  content: '📊';
  margin-right: 6px;
}

.single-reading-badge {
  display: inline-flex;
  align-items: center;
  background: var(--color-warning);
  color: var(--color-background);
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  opacity: 0.9;
}

.single-reading-badge::before {
  content: '🔬';
  margin-right: 6px;
}

.metric-summary {
  display: block;
}

.metric-card.expanded .metric-summary {
  display: none;
}

.metric-detailed {
  display: none;
}

.metric-card.expanded .metric-detailed {
  display: block;
}

.close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  background: var(--color-surface);
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: var(--color-secondary);
  transition: all 0.2s ease;
  z-index: 10;
}

.close-btn:hover {
  background: var(--color-hover-bg);
  color: var(--color-text);
}

.current-value-label {
  font-size: 11px;
  color: var(--color-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.current-value {
  font-size: 32px;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 4px;
}

.current-value-unit {
  font-size: 16px;
  font-weight: 400;
}

.value-status {
  font-size: 12px;
  margin-bottom: 16px;
}

.sparkline-preview {
  height: 30px;
  margin: 12px 0;
  position: relative;
}

.quick-stats {
  display: flex;
  gap: 16px;
  margin-top: 12px;
  padding: 12px;
  background: var(--color-surface);
  border-radius: 6px;
}

.quick-stat {
  flex: 1;
  text-align: center;
}

.quick-stat-label {
  font-size: 10px;
  color: var(--color-muted);
  text-transform: uppercase;
  margin-bottom: 4px;
}

.quick-stat-value {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text);
}

.click-hint {
  text-align: center;
  color: var(--color-muted);
  font-size: 11px;
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.metric-card.expanded .click-hint {
  display: none;
}

.trend-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.trend-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.trend-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
}

.trend-indicator.up {
  background: var(--color-success);
  color: var(--color-background);
  opacity: 0.9;
}

.trend-indicator.down {
  background: var(--color-error);
  color: var(--color-background);
  opacity: 0.9;
}

.trend-indicator.stable {
  background: var(--color-surface);
  color: var(--color-text);
}

.chart-canvas-container {
  position: relative;
  height: 250px;
  margin-top: 12px;
  margin-bottom: 20px;
}

.detailed-timeline-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 12px;
  margin-top: 20px;
}

.timeline-reading {
  background: var(--color-card-bg);
  border: 2px solid var(--color-border);
  border-radius: 8px;
  padding: 12px;
  text-align: center;
  transition: all 0.2s ease;
}

.timeline-reading:hover {
  border-color: var(--color-accent-blue);
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

.timeline-reading.current {
  position: relative;
}

.timeline-reading.current::before {
  content: 'LATEST';
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--color-text);
  color: var(--color-background);
  font-size: 9px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  letter-spacing: 0.5px;
}

.reading-date {
  font-size: 11px;
  color: var(--color-muted);
  margin-bottom: 8px;
  font-weight: 500;
}

.reading-value {
  font-size: 20px;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 4px;
}

.reading-unit {
  font-size: 11px;
  color: var(--color-secondary);
}

.reading-status {
  font-size: 10px;
  margin-top: 6px;
  padding: 3px 8px;
  border-radius: 10px;
  font-weight: 600;
}

.reading-status.normal {
  background: var(--color-success);
  color: var(--color-background);
  opacity: 0.9;
}

.reading-status.low,
.reading-status.high {
  background: var(--color-warning);
  color: var(--color-background);
  opacity: 0.9;
}

.reading-status.critical {
  background: var(--color-error);
  color: var(--color-background);
  opacity: 0.9;
}

.mini-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid var(--color-border);
}

.mini-stat {
  text-align: center;
}

.mini-stat-label {
  font-size: 10px;
  color: var(--color-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.mini-stat-value {
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text);
}

.mini-stat-value.high {
  color: var(--color-success);
}

.mini-stat-value.low {
  color: var(--color-error);
}

.single-reading-content {
  text-align: center;
  padding: 24px 0;
}

.single-reading-visual {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 20px 0;
}

.reading-circle {
  width: 160px;
  height: 160px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  background: var(--color-surface);
  border: 6px solid var(--color-border);
  box-shadow: var(--shadow-lg);
}

.reading-circle.good {
  border-color: var(--color-success);
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%);
}

.reading-circle.warning {
  border-color: var(--color-warning);
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%);
}

.reading-circle.danger {
  border-color: var(--color-error);
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%);
}

.reading-circle-value {
  font-size: 42px;
  font-weight: 800;
  color: var(--color-text);
  line-height: 1;
  margin-bottom: 4px;
}

.reading-circle-unit {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-secondary);
}

.reading-circle-status {
  position: absolute;
  bottom: -12px;
  background: var(--color-card-bg);
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  box-shadow: var(--shadow-md);
}

.reading-circle-status.good {
  color: var(--color-success);
  border: 2px solid var(--color-success);
}

.reading-circle-status.warning {
  color: var(--color-warning);
  border: 2px solid var(--color-warning);
}

.reading-circle-status.danger {
  color: var(--color-error);
  border: 2px solid var(--color-error);
}

.single-reading-info {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid var(--color-border);
}

.single-info-item {
  text-align: center;
  padding: 12px;
  background: var(--color-surface);
  border-radius: 8px;
}

.single-info-label {
  font-size: 10px;
  color: var(--color-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
  font-weight: 600;
}

.single-info-value {
  font-size: 16px;
  font-weight: 700;
  color: var(--color-text);
}

.no-trend-message {
  background: rgba(245, 158, 11, 0.1);
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 12px;
  color: var(--color-text);
  text-align: center;
  margin-top: 16px;
  border-left: 4px solid var(--color-warning);
  font-weight: 600;
  border: 1px solid rgba(245, 158, 11, 0.3);
}

.range-visualization {
  margin-bottom: 12px;
  margin-top: 20px;
}

.range-labels {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: var(--color-muted);
  margin-bottom: 8px;
}

.range-bar {
  height: 8px;
  background: linear-gradient(to right, var(--color-error) 0%, var(--color-error) 5%, var(--color-success) 5%, var(--color-success) 95%, var(--color-error) 95%, var(--color-error) 100%);
  border-radius: 4px;
  position: relative;
  margin-bottom: 12px;
}

.range-marker {
  position: absolute;
  width: 3px;
  height: 16px;
  background: var(--color-text);
  border-radius: 2px;
  top: -4px;
  transform: translateX(-50%);
}

.range-values {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: var(--color-secondary);
}

.status-box {
  padding: 12px;
  border-radius: 4px;
  margin-top: 16px;
}

.status-box.good {
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.3);
}

.status-box.warning {
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.3);
}

.status-box.danger {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.status-box-title {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 4px;
  color: var(--color-text);
}

.status-box-text {
  font-size: 12px;
  color: var(--color-text-secondary);
}

.range-visualization.qualitative .range-bar-qualitative {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: var(--color-surface);
  border-radius: 4px;
}

.range-status-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.range-status-indicator.good {
  background: var(--color-success);
}

.range-status-indicator.warning {
  background: var(--color-warning);
}

.range-status-indicator.danger {
  background: var(--color-error);
}

.narrative-card {
  cursor: pointer;
}

.narrative-card:hover {
  border-color: var(--color-primary);
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.metric-card:nth-child(1) { animation-delay: 0.05s; }
.metric-card:nth-child(2) { animation-delay: 0.1s; }
.metric-card:nth-child(3) { animation-delay: 0.15s; }
.metric-card:nth-child(4) { animation-delay: 0.2s; }

@media (max-width: 768px) {
  .metrics-grid-container {
    grid-template-columns: 1fr !important;
  }

  .detailed-timeline-grid {
    grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  }

  .mini-stats {
    grid-template-columns: repeat(2, 1fr);
  }

  .chart-canvas-container {
    height: 200px;
    min-height: 180px;
  }
}

/* Table scroll wrapper for mobile */
.table-scroll-wrapper {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table-scroll-wrapper table {
  min-width: 500px;
}
`}</style>
    </div>
  );
};
